import { Input } from "@/components/ui/input";
import { formatCurrency } from "@/lib/currencies/currenciesStore";
import { ProductImageThumb } from "@/components/catalog/ProductImageThumb";
import {
  SALE_PRODUCT_SEARCH_DEBOUNCE_MS,
  SALE_PRODUCT_SEARCH_MIN_CHARS,
  searchSaleProducts,
  type SaleProductPick,
} from "@/lib/sales/saleProductSearchStore";
import { cn } from "@/lib/utils";
import { Loader2, Search, X } from "lucide-react";
import { useEffect, useRef, useState, type MouseEvent } from "react";

type Props = {
  value: SaleProductPick | null;
  onChange: (product: SaleProductPick | null) => void;
  disabled?: boolean;
  inputClassName?: string;
  /** Comma-separated inventory statuses (default: in_stock only). */
  inventoryStatus?: string;
  placeholder?: string;
  minCharsHint?: string;
  emptyMessage?: string;
  /**
   * Minimum characters before searching.
   * Use 0 to list matching inventory (e.g. in-stock cars) as soon as the field opens.
   */
  minChars?: number;
};

function ProductThumbSlot({
  src,
  alt,
  className,
  iconClassName,
}: {
  src: string | null | undefined;
  alt: string;
  className?: string;
  iconClassName?: string;
}) {
  return (
    <div
      className={cn(
        "shrink-0 overflow-hidden rounded border border-border/50 bg-muted",
        className,
      )}
    >
      <ProductImageThumb src={src} alt={alt} iconClassName={iconClassName} />
    </div>
  );
}

export function SaleProductPicker({
  value,
  onChange,
  disabled,
  inputClassName,
  inventoryStatus = "in_stock",
  placeholder = "Type at least 2 characters to search…",
  minCharsHint,
  emptyMessage,
  minChars = SALE_PRODUCT_SEARCH_MIN_CHARS,
}: Props) {
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hits, setHits] = useState<SaleProductPick[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);
  const requiredChars = Math.max(0, minChars);
  const searchScopeHint =
    minCharsHint ??
    (requiredChars <= 0
      ? inventoryStatus === "in_stock"
        ? "Showing in-stock cars. Type to filter the list."
        : "Showing available products. Type to filter the list."
      : inventoryStatus === "in_stock"
        ? `Type at least ${requiredChars} characters to search in-stock products.`
        : `Type at least ${requiredChars} characters to search products.`);
  const noResultsMessage = emptyMessage ?? (inventoryStatus === "in_stock" ? "No in-stock products found." : "No products found.");

  useEffect(() => {
    if (value) return;
    const q = search.trim();
    if (!open || q.length < requiredChars) {
      setHits([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    let cancelled = false;
    const delay = q.length === 0 ? 0 : SALE_PRODUCT_SEARCH_DEBOUNCE_MS;
    const timer = window.setTimeout(() => {
      void (async () => {
        try {
          const { data } = await searchSaleProducts(q, 1, { inventoryStatus });
          if (!cancelled) setHits(data);
        } catch {
          if (!cancelled) setHits([]);
        } finally {
          if (!cancelled) setLoading(false);
        }
      })();
    }, delay);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [search, open, value, inventoryStatus, requiredChars]);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const clearSelection = (e?: MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (disabled) return;
    onChange(null);
    setSearch("");
    setHits([]);
    setOpen(true);
  };

  return (
    <div ref={containerRef} className="relative">
      {value ? (
        <div
          className={cn(
            "group relative flex items-center justify-between gap-4 rounded-xl border border-primary/20 bg-gradient-to-r from-primary/[0.04] via-card to-card p-3 shadow-sm transition-all hover:border-primary/40",
            disabled && "opacity-60",
            inputClassName,
          )}
          aria-label={`Selected product: ${value.name}`}
        >
          <div className="flex min-w-0 items-center gap-3.5">
            <ProductThumbSlot
              src={value.thumbnailUrl}
              alt={value.name}
              className="h-14 w-20 shrink-0 rounded-lg shadow-inner"
              iconClassName="h-6 w-6 text-muted-foreground"
            />
            <div className="min-w-0 space-y-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="truncate font-display text-base font-semibold text-foreground">
                  {value.name}
                </span>
                <span className="inline-flex items-center rounded-full border border-emerald-500/30 bg-emerald-500/15 px-2.5 py-0.5 text-[11px] font-semibold text-emerald-700 dark:text-emerald-400">
                  In Stock
                </span>
              </div>
              {value.salePrice != null && Number.isFinite(value.salePrice) && value.salePrice > 0 ? (
                <p className="text-xs font-medium tabular-nums text-muted-foreground">
                  Expected sale price:{" "}
                  <span className="font-semibold text-foreground">
                    {formatCurrency(value.salePrice)}
                  </span>
                </p>
              ) : (
                <p className="text-xs text-muted-foreground">Product ready for sale</p>
              )}
            </div>
          </div>
          {!disabled ? (
            <button
              type="button"
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-border/60 bg-muted/30 text-muted-foreground transition-colors hover:border-destructive/40 hover:bg-destructive/10 hover:text-destructive"
              aria-label="Clear selected product"
              title="Clear product selection"
              onClick={clearSelection}
            >
              <X className="h-4 w-4" aria-hidden />
            </button>
          ) : null}
        </div>
      ) : (
        <>
          <Search className="pointer-events-none absolute left-3 top-1/2 z-10 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            className={cn("glass-input h-10 w-full pl-9 text-sm", inputClassName)}
            value={search}
            disabled={disabled}
            autoComplete="off"
            aria-autocomplete="list"
            aria-expanded={open}
            placeholder={placeholder}
            onChange={(e) => {
              setSearch(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            onKeyDown={(e) => {
              if (e.key === "Escape") setOpen(false);
            }}
          />
        </>
      )}
      {open && !value ? (
        <div
          role="listbox"
          className="absolute left-0 right-0 top-full z-[200] mt-1 max-h-[min(24rem,70vh)] overflow-y-auto rounded-md border bg-popover text-popover-foreground shadow-md"
        >
          {search.trim().length < requiredChars ? (
            <div className="px-3 py-4 text-center text-sm text-muted-foreground">{searchScopeHint}</div>
          ) : loading ? (
            <div className="flex items-center justify-center gap-2 px-3 py-4 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 shrink-0 animate-spin" aria-hidden />
              {search.trim() ? "Searching…" : "Loading in-stock cars…"}
            </div>
          ) : hits.length === 0 ? (
            <div className="px-3 py-4 text-center text-sm text-muted-foreground">{noResultsMessage}</div>
          ) : (
            <ul className="p-1">
              {requiredChars <= 0 && !search.trim() ? (
                <li className="px-2 py-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                  In-stock cars — type to filter
                </li>
              ) : null}
              {hits.map((p) => (
                <li key={p.id}>
                  <button
                    type="button"
                    role="option"
                    className="flex w-full items-center gap-3 rounded-sm px-2 py-2 text-left text-sm outline-none hover:bg-accent hover:text-accent-foreground focus-visible:bg-accent focus-visible:text-accent-foreground"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      onChange(p);
                      setSearch("");
                      setHits([]);
                      setOpen(false);
                    }}
                  >
                    <ProductThumbSlot src={p.thumbnailUrl} alt={p.name} className="h-12 w-16" iconClassName="h-5 w-5" />
                    <div className="min-w-0 flex-1">
                      <span className="block truncate font-medium text-foreground">{p.name}</span>
                      {p.salePrice != null && Number.isFinite(p.salePrice) ? (
                        <span className="text-xs text-muted-foreground tabular-nums">
                          Sale price: {p.salePrice.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      ) : null}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </div>
  );
}
